
import { Component } from '@theme/component';
import { debounce, lockScroll, onDocumentLoaded, setHeaderMenuStyle, unlockScroll } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';
 
/**
 * A custom element that manages a header menu.
 *
 * Activation is click-based (not hover): clicking a top-level item with a
 * submenu opens it; clicking it again, clicking outside, or pressing Escape
 * closes it. Items without a submenu (e.g. "Sale") behave as plain links.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item (the trigger <a>/<button>).
 * @property {HTMLElement | null} activeOverflowItem - The overflow item shown when the More trigger is active.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];
 
  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;
 
  connectedCallback() {
    super.connectedCallback();
 
    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    document.addEventListener('click', this.#onDocumentClick, true);
    document.addEventListener('keydown', this.#onDocumentKeydown);
  }
 
  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    document.removeEventListener('click', this.#onDocumentClick, true);
    document.removeEventListener('keydown', this.#onDocumentKeydown);
    this.#cleanupMutationObserver();
    // Never leave the page locked behind a menu that no longer exists
    unlockScroll(this);
  }
 
  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);
 
  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
    activeOverflowItem: null,
  };
 
  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }
 
  /**
   * Find the first overflowing menu item shown by the More trigger.
   * @returns {HTMLElement | null}
   */
  #getFirstOverflowMenuItem() {
    const menuItem = this.refs.overflowMenu?.querySelector('[slot="overflow"] [ref="menuitem"]');
    return menuItem instanceof HTMLElement ? menuItem : null;
  }
 
  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }
 
  /**
   * Click handler bound directly to a top-level menu trigger (the element
   * with `ref="menuitem"`). Opens its submenu if it's not already open, or
   * closes it if it is (a toggle). Items with no submenu are left alone so
   * they navigate normally -- this is what makes a plain link like "Sale"
   * just work with zero extra configuration.
   * @param {MouseEvent} event
   */
  toggle = (event) => {
    // This handler is only ever bound (in the template) to triggers that DO have
    // a submenu, so stop the navigation unconditionally and first -- before any
    // other logic that could fail/return early and accidentally let it through.
    event.preventDefault();
 
    if (!this.headerComponent) return;
    if (!(event.target instanceof Element)) return;
 
    // The theme's `on:click` binding uses event delegation, so `event.currentTarget`
    // does NOT reliably point at the trigger that was clicked -- resolve it from
    // `event.target` instead, same as the rest of this theme's components do.
    const item = /** @type {HTMLElement | null} */ (event.target.closest('[ref="menuitem"]'));
    if (!item) return;
 
    // The `slot` attribute (used to distinguish default / "more" / "overflow"
    // items) lives on the <li>, not on the trigger itself.
    const slot = item.closest('li')?.slot ?? '';
    const isMoreTrigger = slot === 'more';
    const overflowItem = isMoreTrigger ? this.#getFirstOverflowMenuItem() : null;
    const submenu = findSubmenu(item) || (isMoreTrigger && findSubmenu(overflowItem) ? this.overflowMenu : null);
 
    // Nothing to open (shouldn't normally happen since this handler is only
    // bound when a submenu exists) -- bail out having already blocked navigation.
    if (!submenu) return;
 
    if (item === this.#state.activeItem) {
      this.#deactivate();
    } else {
      this.#activate(item, overflowItem, slot === '');
    }
  };
 
  /**
   * Activate the given menu item, opening its submenu.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} overflowItem
   * @param {boolean} isDefaultSlot
   */
  #activate(item, overflowItem, isDefaultSlot) {
    this.dataset.overflowExpanded = (!isDefaultSlot).toString();
 
    const previouslyActiveItem = this.#state.activeItem;
    const previouslyActiveOverflowItem = this.#state.activeOverflowItem;
 
    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }
    if (previouslyActiveOverflowItem && previouslyActiveOverflowItem !== previouslyActiveItem) {
      previouslyActiveOverflowItem.ariaExpanded = 'false';
    }
 
    this.#state.activeItem = item;
    this.#state.activeOverflowItem = overflowItem;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';
    if (overflowItem && overflowItem !== item) {
      overflowItem.ariaExpanded = 'true';
    }
 
    const overflowItemSubmenu = overflowItem ? findSubmenu(overflowItem) : null;
    let submenu = findSubmenu(item) || (overflowItemSubmenu ? this.overflowMenu : null);
    const hasSubmenu = Boolean(submenu);
 
    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }
 
    if (submenu) {
      this.dispatchEvent(new MegaMenuHoverEvent());
 
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';
 
      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();
 
      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });
 
      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }
 
    let finalHeight = submenu?.offsetHeight || 0;
 
    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }
 
    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }
 
    const headerVisibleHeight = this.#getHeaderVisibleHeight();
 
    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight, headerVisibleHeight);
    this.style.setProperty('--submenu-opacity', '1');
 
    /*
     * The mega menu panel covers the viewport, so let the page behind it stay put
     * (alessi.com behaviour). `lockScroll` is reference-counted per owner, so it
     * plays nicely with the cart drawer, search modal, etc.
     */
    if (finalHeight > 0) {
      lockScroll(this);
    } else {
      unlockScroll(this);
    }
  }
 
  /**
   * Close the currently open menu, if any.
   */
  #deactivate = () => {
    const item = this.#state.activeItem;
    if (!item) return;
 
    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0, 0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';
 
    const submenu = findSubmenu(item) || (item.closest('[slot="more"]') ? this.overflowMenu : null);
    const activeOverflowItem = this.#state.activeOverflowItem;
 
    this.#state.activeItem = null;
    this.#state.activeOverflowItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    if (activeOverflowItem && activeOverflowItem !== item) {
      activeOverflowItem.ariaExpanded = 'false';
    }
 
    unlockScroll(this);
 
    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };
 
  /**
   * Public entry point for a dedicated "close" control (e.g. the mega menu's
   * close button) to shut whatever menu is currently open.
   */
  close = () => {
    this.#deactivate();
  };
 
  /**
   * Close the open menu when clicking anywhere outside of it.
   * @param {MouseEvent} event
   */
  #onDocumentClick = (event) => {
    if (!this.#state.activeItem) return;
    if (!(event.target instanceof Node)) return;
    if (this.contains(event.target)) return;
 
    this.#deactivate();
  };
 
  /**
   * Close the open menu on Escape, returning focus to its trigger.
   * @param {KeyboardEvent} event
   */
  #onDocumentKeydown = (event) => {
    if (event.key !== 'Escape' || !this.#state.activeItem) return;
 
    const trigger = this.#state.activeItem;
    this.#deactivate();
    trigger.focus();
  };
 
  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;
 
    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };
 
    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }
 
  /**
   * Read the visible header height before submenu height writes invalidate layout.
   * @returns {number}
   */
  #getHeaderVisibleHeight() {
    if (!this.headerComponent) return 0;
 
    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');
 
    return isOverlapSituation && this.headerComponent.offsetHeight > 0
      ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
      : this.headerComponent.offsetHeight;
  }
 
  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   * @param {number} headerVisibleHeight
   */
  #setFullOpenHeaderHeight(submenuHeight, headerVisibleHeight) {
    if (!this.headerComponent) return;
 
    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + headerVisibleHeight;
 
    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }
 
  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
 
  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}
 
if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}
 
/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
 